import type { SandGrid } from '../sandgrid/SandGrid';
import type { WaterGrid } from '../water/WaterGrid';

export class WaterPhysics {
  private flowSpeed: number;
  private erosionRate: number;

  constructor(flowSpeed: number = 2.0, erosionRate: number = 0.1) {
    this.flowSpeed = flowSpeed;
    this.erosionRate = erosionRate;
  }

  public setFlowSpeed(speed: number): void {
    this.flowSpeed = speed;
  }

  public setErosionRate(rate: number): void {
    this.erosionRate = rate;
  }

  // TODO:
  // - water seems to flow into sand, it  should not

  public updateFlow(
    waterGrid: WaterGrid,
    sandGrid: SandGrid,
    deltaTime: number
  ): void {
    // Water heights in the grid represent water DEPTH above sand (relative height)
    // To calculate flow, we need effective surface heights (sand + water depth)
    const newHeights = new Float32Array(waterGrid.width * waterGrid.height);

    // Initialize with current water depths
    for (let i = 0; i < newHeights.length; i++) {
      newHeights[i] = waterGrid.getHeight(i % waterGrid.width, Math.floor(i / waterGrid.width));
    }

    // Clear all velocities before calculating new flow
    waterGrid.apply((x, y) => {
      waterGrid.setVelocity(x, y, 0, 0);
    });

    // Use random-by-line order to reduce directional bias
    waterGrid.apply((x, y) => {
      const idx = waterGrid.getIndex(x, y);
      const waterDepth = newHeights[idx]; // Use current evolving state
      const sandHeight = sandGrid.get(x, y);

      // Only process cells with water
      if (waterDepth <= 0) return;

      // Calculate effective surface height for current cell (sand base + water depth)
      const surfaceHeight = sandHeight + waterDepth;

      // Get neighbor coordinates
      const offsets = [
        { dx: -1, dy: 0 },
        { dx: 1, dy: 0 },
        { dx: 0, dy: -1 },
        { dx: 0, dy: 1 }
      ];

      const lowerNeighbors: Array<{ x: number; y: number; waterHeight: number }> = [];
      const heightDiffs: number[] = [];

      // Check each neighbor using current newHeights state
      for (const { dx, dy } of offsets) {
        const nx = x + dx;
        const ny = y + dy;

        if (!waterGrid.isValid(nx, ny)) continue;

        const nIdx = waterGrid.getIndex(nx, ny);
        const nWaterHeight = newHeights[nIdx]; // Use evolving state!
        const nSandHeight = sandGrid.get(nx, ny);
        const nSurfaceHeight = nSandHeight + nWaterHeight;

        // Simulate surface tension, liquid harmonizes itself to the lowest surface height,
        // but might not flow to a lower neighbor with no water.
        let shouldFlow = false;
        if (nWaterHeight > 0) {
          // Move to neighbors with water if the surface height is at least 0.05 lower.
          shouldFlow = nSurfaceHeight < surfaceHeight - 0.05;
        } else {
          // Only move to neighbors with no water if the surface height is at least 0.8 lower.
          shouldFlow = nSurfaceHeight < surfaceHeight - 0.8;
        }

        if (shouldFlow) {
          lowerNeighbors.push({ x: nx, y: ny, waterHeight: nWaterHeight });
          heightDiffs.push(surfaceHeight - nSurfaceHeight);
        }
      }

      if (lowerNeighbors.length === 0) return;

      const totalHeightDiff = heightDiffs.reduce((sum, diff) => sum + diff, 0);

      let totalFlow = 0;

      for (let i = 0; i < lowerNeighbors.length; i++) {
        const neighbor = lowerNeighbors[i];
        const heightDiff = heightDiffs[i];
        const flowRatio = heightDiff / totalHeightDiff;

        // Calculate flow amount based on current water depth and height difference
        // TODO: make sure we don't flow more than what's needed to harmonize the surface.
        let flowAmount = Math.min(
          // Flow based on current water depth and flow speed.
          waterDepth * this.flowSpeed * flowRatio * deltaTime * 3.0,
          // At max, equialize height difference.
          heightDiff * 0.9 / 2
        );

        // TODO: don't allow super small flow otherwise it just spreads everywhere.
        if (flowAmount > 0) {
          const nIdx = neighbor.y * waterGrid.width + neighbor.x;
          // Add flow amount to neighbor's water depth (relative height)
          newHeights[nIdx] += flowAmount;
          totalFlow += flowAmount;

          // Update velocity for source cell
          const dx = neighbor.x - x;
          const dy = neighbor.y - y;
          const vel = waterGrid.getVelocity(x, y);
          vel[0] += dx * flowAmount;
          vel[1] += dy * flowAmount;
          waterGrid.setVelocity(x, y, vel[0], vel[1]);
        }
      }

      // Remove flowed water from source cell
      if (totalFlow > 0) {
        const cIdx = waterGrid.getIndex(x, y);
        newHeights[cIdx] -= totalFlow;
      }
    }, 'random-by-line');

    // Apply new heights
    waterGrid.apply((x, y) => {
      const idx = waterGrid.getIndex(x, y);
      waterGrid.setHeight(x, y, newHeights[idx]);
    });
  }

  public applyErosion(
    sandGrid: SandGrid,
    waterGrid: WaterGrid,
    deltaTime: number
  ): void {
    // Water depth is stored as relative height (depth above sand)
    waterGrid.apply((x, y, waterDepth) => {
      const sandHeight = sandGrid.get(x, y);

      // Only erode if there's significant water depth and sand to erode
      if (waterDepth > 1.0 && sandHeight > 0.1) {
        const erosionAmount = Math.min(
          this.erosionRate * waterDepth * deltaTime * 0.1,
          sandHeight * 0.1
        );

        sandGrid.add(x, y, -erosionAmount);
      }
    });
  }

  private getNeighborsWithInfo(
    x: number,
    y: number,
    sandGrid: SandGrid,
    waterGrid: WaterGrid
  ): Array<{ x: number; y: number; waterHeight: number }> {
    const neighbors: Array<{ x: number; y: number; waterHeight: number }> = [];
    const offsets = [
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 }
    ];

    for (const { dx, dy } of offsets) {
      const nx = x + dx;
      const ny = y + dy;

      if (sandGrid.isValid(nx, ny)) {
        neighbors.push({
          x: nx,
          y: ny,
          waterHeight: waterGrid.getHeight(nx, ny)
        });
      }
    }

    return neighbors;
  }
}
